import React, { useEffect, useRef, useState } from 'react';
import axios from "axios";
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';
import { TbSortAscending2Filled } from "react-icons/tb";
import { BsFiletypeCsv } from "react-icons/bs";

import AddEditPopup from './addEditPopup';
import Spinner from '../../components/spinner';
import AddedBy from "../../components/addedBy";
import { useLanguage } from '../../context/languageContext';
import { Tooltip } from 'react-tooltip';
import GenerateCSV from '../../components/popup/generateCSV';
import NoContent from '../../components/notFound/noBooks';
import FilterBar from '../../components/filterBar';
import SortBy from '../../components/sortBy';
import { useFilterChips } from '../../components/filterBar/useFilterChips';
import Pagination from '../../components/pagination';
import { formatMongoDBTime } from '../../utills/dateAndTime';
import { convertToCloudFrontUrl , fetchAllGenresByLangOptions, fetchAllProfessionOptions , fetchAllActorNames } from '../../helper';
import { Can } from "../../context/permissionContext";
import { confirmCascadeDelete } from "../../helper/confirmCascadeDelete";


function FavoriteAuthors() {
  const { adminInfo, isShowCSVModalVisible, setIsShowCSVModalVisible, handleCloseCSV, handleGenerateCSV } = useLanguage();
  const apiParamsRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [selectedAuthorIds, setSelectedAuthorIds] = useState([]);
  const [selectAllAuthors, setSelectAllAuthors] = useState(false);
  const [authorsData, setAuthorsData] = useState(null);
  const [authorId, setAuthorId] = useState([]);

  const [isAddAuthorModalVisible, setIsAddAuthorModalVisible] = useState(false);
  const [isEditAuthorModalVisible, setIsEditAuthorModalVisible] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalNumOfFavoriteAuthors, setTotalNumOfFavoriteAuthors] = useState(0);

  // Search state

  // Sorting state
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  // The Sort by control. Sent on every request, so it is the list's order
  // of record; the column arrow below keeps itself in step with it.
  const [sortBy, setSortBy] = useState('latestCreated');

  const totalPages = Math.ceil(totalNumOfFavoriteAuthors / itemsPerPage);


  // The chip row under the search box. `chipFilters` is already in the

  // shape the endpoint reads, so it merges straight into `apiFilters`.

  const filters = useFilterChips({ supportsMatchAny: true, stickyField: 'authorName' });

  const apiFilters = {
    currentPage,
    itemsPerPage,
    langCode: "en",
    sortOrder: sortOrder,
    sortBy,
    // LAST on purpose: the single-field search above writes '' into every
    // field it is not searching, so a spread placed above it is wiped.
    ...filters.chipFilters,
    matchMode: filters.matchMode,
  };


  // Profession and genre are managed lists, so their filters offer the stored
  // names instead of asking anyone to spell them.
  const [professionNames, setProfessionNames] = useState([]);
  const [genreNames, setGenreNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    Promise.all([
      fetchAllProfessionOptions(adminInfo.token, 'en'),
      fetchAllGenresByLangOptions('en', adminInfo.token),
    ]).then(([professions, genres]) => {
      if (cancelled) return;
      setProfessionNames(professions.map((pr) => pr.label));
      setGenreNames(genres.map((g) => g.genreName));
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);


  // The names behind Created by / Edited by, so those filters are picked from
  // the team rather than spelled from memory.
  const [actorNames, setActorNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllActorNames(adminInfo.token).then((names) => {
      if (!cancelled) setActorNames(names);
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);

  const filterOptions = [
    { value: 'authorName', label: 'Author Name' },
    { value: 'profession', label: 'Profession', suggestions: professionNames },
    { value: 'genres', label: 'Genre', suggestions: genreNames },
    { value: 'createdAt', label: 'Created on' },
    { value: 'updatedAt', label: 'Updated on' },
    { value: 'createdBy', label: 'Created by', suggestions: actorNames },
    { value: 'updatedBy', label: 'Edited by', suggestions: actorNames }
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, sortOrder, sortBy, filters.signature]);

  useEffect(() => {
    if (!adminInfo?.token) {
      return;
    }

    document.title = "Penspace - Favorite Authors";
    const keyValues = JSON.stringify({ currentPage, itemsPerPage, sortOrder, sortBy, filters: filters.signature });

    if (apiParamsRef.current === keyValues) {
      return;
    }

    apiParamsRef.current = keyValues;
    fetchAllFavoriteAuthorsData();
  }, [adminInfo, currentPage, itemsPerPage, sortOrder, sortBy, filters.signature]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };


  const handleSort = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
      // Keeps the arrow live now that Sort by decides the order.
      setSortBy(prev => (prev === 'oldestCreated' ? 'latestCreated' : 'oldestCreated'));
  };

  const fetchAllFavoriteAuthorsData = async () => {
    setLoading(true);

    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/favorite-author/getall`, { filters: apiFilters }, { headers });

      if (response.status) {
        // Normalize author images: convert S3 URLs to CloudFront URLs immediately after API fetch
        const normalizedAuthors = response?.data.favoriteAuthorsDocs?.map(author => {
          if (!author || !author.image) {
            return author;
          }
          
          const normalized = { ...author };
          if (normalized.image?.uploadedImageUrl) {
            normalized.image = {
              ...normalized.image,
              uploadedImageUrl: convertToCloudFrontUrl(normalized.image.uploadedImageUrl)
            };
          }
          
          return normalized;
        }) || [];
        
        setAuthorsData(normalizedAuthors);
        setTotalNumOfFavoriteAuthors(response?.data.totalCount || 0);
      }
    } catch (error) {
      console.error("fetchAllFavoriteAuthorsData catch error::", error);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  const handleSelectAuthorId = (seletedRowId) => {
    if (selectedAuthorIds.includes(seletedRowId)) {
      setSelectedAuthorIds(prevState => prevState.filter(productId => productId !== seletedRowId));
    } else {
      setSelectedAuthorIds(prevState => [...prevState, seletedRowId]);
    }
  };

  const handleSelectAllAuthors = () => {
    if (selectAllAuthors) {
      setSelectedAuthorIds([]);
    } else {
      setSelectedAuthorIds(authorsData?.map((value) => value._id));
    }
    setSelectAllAuthors((prevSelectAll) => !prevSelectAll);
  };

  const handleDeleteRows = async () => {
    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const what = selectedAuthorIds.length === 1 ? "this author" : `${selectedAuthorIds.length} authors`;
      if (!(await confirmCascadeDelete({ source: "author", ids: selectedAuthorIds, what, headers }))) return;

      const { data: response } = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/favorite-author/delete`, { selectedAuthorIds }, { headers });

      if (response.status === true) {
        setSelectedAuthorIds([]);
        setSelectAllAuthors(false);
        fetchAllFavoriteAuthorsData();
        toast.success(response.message || "Author has been deleted.");
      }
    } catch (error) {
      console.error("handleDeleteRows catch error::", error);
    }
  };

  const handleAddShowFavoriteAuthor = () => {
    setIsAddAuthorModalVisible(true);
    setIsEditAuthorModalVisible(false);
  };

  const handleEditShowFavoriteAuthor = (catId) => {
    setAuthorId(catId);
    setIsEditAuthorModalVisible(true);
  };

  const handleCloseModalPopup = () => {
    setIsAddAuthorModalVisible(false);
    setIsEditAuthorModalVisible(false);
  };


  return (
    <React.Fragment>
      <div className="heading">
        <h4>
          {totalNumOfFavoriteAuthors > 0 ? `Favorite Authors (${totalNumOfFavoriteAuthors})` : `Favorite Author`}
        </h4>
        <div className="d-flex gap-2">
          {selectedAuthorIds.length > 0 ? (
            <Can feature="authors" action="delete"><button type="button" className="btn btn-outline-danger" onClick={handleDeleteRows}>Delete</button></Can>
          ) : (
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-dark" onClick={() => setIsShowCSVModalVisible(true)}>Generate CSV <BsFiletypeCsv /></button>
              <Can feature="authors" action="create"><button type="button" className="btn btn-dark" onClick={handleAddShowFavoriteAuthor}>Add Author</button></Can>
            </div>
          )}
        </div>
      </div>
      <div className='d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center'>
        <SortBy value={sortBy} onChange={setSortBy} />
        {totalPages > 1 && (
          <div className='d-flex justify-content-center justify-content-lg-end w-100 w-lg-auto ms-lg-auto'>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalNumOfFavoriteAuthors}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
              showTotalItems={true}
            />
          </div>
        )}
      </div>
      <FilterBar
      options={filterOptions}
      // getAllFavoriteAuthors builds its clauses as $or branches; the
      // other list endpoints do not yet, so only this page offers it.
      supportsMatchAny
      chips={filters.chips}
      matchMode={filters.matchMode}
      onMatchModeChange={filters.setMatchMode}
      onAdd={filters.addChip}
      onUpdate={filters.updateChip}
      onRemove={filters.removeChip}
      onClear={filters.clearChips}
      />
      {loading ? (
        <Spinner />
      ) : (
        <div className="table_scrollbar table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    className="box form-check-input"
                    type="checkbox"
                    checked={selectAllAuthors}
                    onChange={handleSelectAllAuthors}
                  />
                </th>
                <th>S.no</th>
                <th>Author Name
                  <TbSortAscending2Filled 
                    size={18} 
                    style={{ cursor: 'pointer', marginLeft: '3px' }}
                    onClick={handleSort}
                    title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                  />
                </th>
                <th>Profession</th>
                {/* Derived from the books this author wrote, and kept in step
                    server-side — see syncAuthorGenresFromBooks. Stored rather
                    than joined at read time so the Genre FILTER, which searches
                    this same field, agrees with what the column shows. */}
                <th>Genre</th>
                {/* <th className={styles.books}>Books Written</th> */}
                <th>About</th>
                <th>Created on</th>
                <th>Updated on</th>
                <th>Added by</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {authorsData?.length > 0 ? (
                authorsData.map((author, index) => {
                  const isSelectedAuthorValues = selectedAuthorIds.some((selectedId) => selectedId === author._id);
                  const showSelectedLanguage = author.translations;
                  const globalIndex = (currentPage - 1) * itemsPerPage + index;

                  return (
                    author && (
                      <tr key={author._id}>
                        <td className="checkBox">
                          <input
                            className="box form-check-input"
                            type="checkbox"
                            checked={isSelectedAuthorValues}
                            onChange={() => handleSelectAuthorId(author._id)}
                          />
                        </td>
                        <td className="index">{globalIndex + 1}</td>
                        <td className="max_width">{showSelectedLanguage.authorName}</td>
                        <td className="max_width">
                          {showSelectedLanguage.profession?.map(item => (
                            <div className="list_styled" key={item.professionId}>{item.professionName}</div>
                          ))}
                        </td>
                        <td className="max_width">
                          {showSelectedLanguage.genres?.map(item => (
                            <div className="list_styled" key={item.genreId}>{item.genreName}</div>
                          ))}
                        </td>
                        {/* <td className={styles.books}>{authorByLang.booksWritten}</td> */}
                        <td className="tooltip_content">
                          <span
                            data-tooltip-id={`tooltip-${index}`}
                            data-tooltip-content={showSelectedLanguage.about}
                            data-tooltip-place="top"
                          >
                            {showSelectedLanguage.about}
                          </span>
                          <Tooltip
                            id={`tooltip-${index}`}
                            className="custom-tooltip"
                            delayShow={200}
                            offset={10}
                            float={true}
                          />
                        </td>
                        <td className="min_width">{formatMongoDBTime(author.createdAt)}</td>
                        <td className="min_width">{author.updatedAt ? formatMongoDBTime(author.updatedAt) : "—"}</td>
                        <td><AddedBy record={author} /></td>
                        <td className="action">
                          <div onClick={() => handleEditShowFavoriteAuthor(author._id)}><FaEdit size={20} /></div>
                        </td>
                      </tr>
                    )
                  );
                })
              ) : (
                <NoContent title="favorite authors" colSpan={10} />
              )}
            </tbody>
          </table>
        </div>
      )}

      <AddEditPopup
        isAddAuthorModalVisible={isAddAuthorModalVisible}
        setIsAddAuthorModalVisible={setIsAddAuthorModalVisible}
        isEditAuthorModalVisible={isEditAuthorModalVisible}
        handleCloseModal={handleCloseModalPopup}
        authorId={authorId}
        onSuccess={fetchAllFavoriteAuthorsData}
      />

      <GenerateCSV
        title="favorite authors"
        isShowCSVModalVisible={isShowCSVModalVisible}
        handleCloseCSV={handleCloseCSV}
        handleGenerateCSV={() => handleGenerateCSV("favorite-authors", apiFilters)}
      />
    </React.Fragment>
  );
}

export default FavoriteAuthors
